'use client';

import type { Content } from '@prismicio/client';
import { JSXMapSerializer, PrismicRichText } from '@prismicio/react';
import { usePathname } from 'next/navigation';
import type { Simplify } from '../../../../prismicio-types';
import { Button, Container } from '@/components';

import { PersonalLoansVideo } from './PersonalLoansVideo/PersonalLoansVideo';
import { PersonalLoansCards } from './PersonalLoansCards/PersonalLoansCards';
import { useVisible } from '@/hooks';
import { getPageName } from '@/utils';
import { routes } from '@/constants';
import NextButton from '@/components/Button/NextButton';
import { ProspectData } from '@/types/salesforce';
import { MODAL_NAMES } from '@/constants/modals';
import useModal from '@/hooks/useModal';
import styles from './personalLoansInner.module.scss';

interface PersonalLoansInnerProps {
  data: Simplify<Content.PersonalLoansSliceDefaultPrimary>;
  prospect?: ProspectData;
  variant: 'default' | 'engineLanding';
}

const components: JSXMapSerializer = {
  heading2: ({ children }) => <h2 className={styles.title}>{children}</h2>,
};

export const PersonalLoansInner = ({
  data,
  prospect,
  variant,
}: PersonalLoansInnerProps) => {
  const [isVisible, id] = useVisible();
  const path = usePathname();
  const pageName = getPageName(path);
  const isPreQualifiedPage = pageName === routes.preQualified;
  const { openModal } = useModal();

  const isVisibleStyles = isVisible ? styles.isVisible : '';
  return (
    <Container styles={styles.container} id={id}>
      <div className={`${styles.title_wrapper} ${isVisibleStyles}`}>
        <PrismicRichText field={data.title} components={components} />
      </div>
      <p className={`${styles.subtitle} ${isVisibleStyles}`}>{data.subtitle}</p>
      <div className={`${styles.button_wrapper} ${isVisibleStyles}`}>
        {isPreQualifiedPage ? (
          <NextButton
            variation="white"
            styles={styles.button}
            text={data.button_text as string}
            type="button"
            onClick={() => openModal({ name: MODAL_NAMES.preQualified })}
          />
        ) : variant === 'engineLanding' ? (
          <NextButton
            type="button"
            buttonType="button"
            styles={styles.button}
            text="Click to finalize and review your offer"
            isArrow
            variation="white"
            onClick={() =>
              openModal({
                name: MODAL_NAMES.engine,
                data: { prospect: prospect || null },
              })
            }
          />
        ) : (
          <Button
            styles={styles.button}
            text={data.button_text}
            link={data.button_link}
            variation="white"
          />
        )}
      </div>
      <PersonalLoansVideo />
      <PersonalLoansCards
        heading={data.about_heading}
        buttonText={data.about_button_text}
        link={data.about_button_link}
        cards={data.about_items}
        variant={variant}
        prospect={prospect}
      />
    </Container>
  );
};
